import React, { useMemo, useState } from "react";
import { MdHelpOutline } from "react-icons/md";
import { Tooltip } from "../../../../components/shared/Tooltip/Tooltip";
import {
  buildMetric,
  formatPrimaryValue,
  formatTourDeviation,
  formatZScore,
  getDeviationColorClass,
  summarizeIndicatorsByClub,
} from "../../../swing_library/utils/metrics";
import { useSwingLibrarySettings } from "../../../swing_library/hooks/useSwingLibrarySettings";
import { useSwingLibraryStore } from "../../../swing_library/store/swingLibraryStore";
import { useSwingList } from "../../../swing_library/hooks/useSwingList";
import { LOCKED_COLUMN_COUNT } from "../../../swing_library/components/Table/constants/lockedColumnCount";

export default function SummaryRow({
  backgroundClass,
  orderedMetricColumns,
  rowKey,
  title,
  videos,
}) {
  const { data: swingListData } = useSwingList();

  const { data: swingLibrarySettings } = useSwingLibrarySettings();

  const { activeClub } = useSwingLibraryStore();

  const summary = useMemo(() => {
    return summarizeIndicatorsByClub(
      videos ?? [],
      undefined,
      swingListData?.databaseReference ?? undefined
    );
  }, [videos, swingListData?.databaseReference]);

  const clubKey = activeClub === "D" ? "Driver" : "Iron";
  const clubSummary = summary?.[clubKey] ?? {};

  return (
    <tr
      className={`border-collapse ${backgroundClass ?? ""}`}
      key={rowKey}
      style={{ height: "75px" }}
    >
      <td className="relative px-2 text-start" colSpan={LOCKED_COLUMN_COUNT}>
        <div className="flex flex-col">
          <div className="font-manrope text-gray900 text-sm font-bold">
            {title}
          </div>

          <div className="flex items-center gap-1">
            <div className="font-manrope text-gray600 text-sm font-medium">
              Z Score
            </div>

            <Tooltip
              content={
                <div className="text-sm text-white">
                  The Z-Score measures how many standard deviations this swing
                  is away from your average
                </div>
              }
            >
              <MdHelpOutline className="text-gray500 size-4 cursor-pointer" />
            </Tooltip>
          </div>
          {swingLibrarySettings?.tourAveragesToggle && (
            <div className="font-manrope text-gray600 text-sm font-medium">
              Deviation from Tour Avg
            </div>
          )}
        </div>
        <div className="bg-gray200 absolute top-0 right-0 h-full w-px" />
      </td>

      {orderedMetricColumns.map((col) => {
        const metric = buildMetric(clubSummary, col.id);
        const unit = col.unit ?? undefined;
        const primaryClass = swingLibrarySettings?.tourAveragesToggle
          ? getDeviationColorClass(metric)
          : "text-gray900";

        return (
          <td className="relative px-2 text-start" key={`${rowKey}-${col.id}`}>
            <div className="flex flex-col items-start justify-center">
              <div className={`text-sm font-semibold ${primaryClass}`}>
                {formatPrimaryValue(metric, unit)}
              </div>
              <div className="text-sm font-semibold text-black">
                {formatZScore(metric, { dp: 0 })}
              </div>
              {swingLibrarySettings?.tourAveragesToggle && (
                <div className="text-gray600 text-sm">
                  {formatTourDeviation(metric, unit)}
                </div>
              )}
            </div>
            <div className="bg-gray200 absolute top-0 right-0 h-full w-px" />
          </td>
        );
      })}
    </tr>
  );
}
